import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, MessageCircle } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';

const Navbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  
  const isActive = (path) => location.pathname === path;
  
  // Pages that have light/white backgrounds and need dark navbar text
  const isLightBackgroundPage = location.pathname === '/admin' || 
                                 location.pathname === '/emi-calculator' ||
                                 location.pathname === '/privacy-policy' ||
                                 location.pathname === '/terms-conditions';
  
  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const menuItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/properties', label: 'Our Projects' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact Us' },
  ];
  
  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled 
            ? 'bg-black/95 shadow-lg' 
            : isLightBackgroundPage
              ? 'bg-white shadow-md'
              : 'bg-transparent'
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo with oval background */}
            <Link 
              to="/" 
              className={`flex items-center relative md:mb-0 transition-opacity duration-500 ${location.pathname === '/' && !scrolled ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} 
              data-testid="logo-link"
            >
              <div className="bg-white rounded-full shadow-2xl flex items-center justify-center overflow-hidden w-10 h-10 md:w-16 md:h-16" style={{ marginTop: '0px' }}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_gk-properties/artifacts/bexs4tg2_file_00000000f03c7209b29acd69cf9894f8.png" 
                  alt="GK Group of Properties Logo" 
                  className="w-[85%] h-[85%] object-contain"
                />
              </div>
            </Link>
            
            {/* Desktop Navigation Links */}
            <div className="hidden lg:flex items-center gap-8">
              {menuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`uppercase tracking-widest text-xs font-bold transition-all duration-300 ${
                    isActive(item.path)
                      ? 'text-gray-400'
                      : ''
                  }`}
                  style={!isActive(item.path) ? { color: '#B79657' } : {}}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            
            {/* Right side buttons */}
            <div className="flex items-center gap-2 md:gap-3">
              <Link to="/enquiry" className="hidden sm:block">
                <Button 
                  className="bg-secondary hover:bg-secondary/90 text-secondary-foreground h-9 md:h-10 px-4 md:px-6 text-xs md:text-sm font-medium transition-all duration-300 rounded-full whitespace-nowrap"
                  data-testid="enquire-now-btn"
                >
                  Enquire Now
                </Button>
              </Link>
              
              <a 
                href="https://wa.me/919114993033" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-green-500 hover:bg-green-600 flex items-center justify-center transition-colors duration-300 flex-shrink-0"
                data-testid="whatsapp-btn"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-4 h-4 md:w-5 md:h-5 text-white" strokeWidth={2} fill="white" />
              </a>
              
              <a 
                href="tel:+917008078580"
                className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-colors duration-300 flex-shrink-0 ${
                  scrolled || !isLightBackgroundPage
                    ? 'bg-white/20 hover:bg-white/30'
                    : 'bg-primary/10 hover:bg-primary/20'
                }`}
                data-testid="phone-btn"
                aria-label="Call"
              >
                <Phone className={`w-4 h-4 md:w-5 md:h-5 ${
                  scrolled || !isLightBackgroundPage ? 'text-white' : 'text-primary'
                }`} strokeWidth={2} />
              </a>
              
              {/* Mobile menu button - only visible on mobile/tablet */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary hover:bg-primary/90 flex items-center justify-center transition-colors duration-300 flex-shrink-0"
                data-testid="menu-toggle-btn"
                aria-label="Toggle menu"
              >
                {isOpen ? (
                  <X className="w-4 h-4 md:w-5 md:h-5 text-primary-foreground" strokeWidth={2} />
                ) : (
                  <Menu className="w-4 h-4 md:w-5 md:h-5 text-primary-foreground" strokeWidth={2} />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-16 md:top-20 left-0 right-0 z-40 bg-white border-b border-gray-200 shadow-lg lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6">
              <div className="flex flex-col gap-2">
                {menuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`p-4 rounded-sm transition-all duration-300 uppercase tracking-widest text-xs font-medium ${
                      isActive(item.path)
                        ? 'bg-gray-100 text-gray-500'
                        : 'bg-white hover:bg-gray-50'
                    }`}
                    style={!isActive(item.path) ? { color: '#B79657' } : {}}
                    data-testid={`menu-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {item.label}
                  </Link>
                ))}
                <Link
                  to="/enquiry"
                  onClick={() => setIsOpen(false)}
                  className="sm:hidden p-4 rounded-sm bg-secondary text-secondary-foreground uppercase tracking-widest text-xs font-medium text-center mt-2"
                >
                  Enquire Now
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
